/**
 * Scoped styles for the widget's shadow root.
 *
 * Strategy:
 * - :host { all: initial } wipes inherited styles from the host page,
 *   preventing CSS leakage in either direction.
 * - All theming via CSS variables on :host so the component is easy to
 *   re-skin (Phase 2 will set these from PublicBotConfig).
 * - High z-index so the widget stays above any site chrome.
 */

export const styles = `
:host {
  all: initial;
  --apaxir-primary: #0093b2;
  --apaxir-primary-contrast: #ffffff;
  --apaxir-bg: #ffffff;
  --apaxir-bg-muted: #f5f7f9;
  --apaxir-text: #1f2937;
  --apaxir-text-muted: #6b7280;
  --apaxir-border: #e5e7eb;
  --apaxir-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  --apaxir-radius: 14px;
  --apaxir-font: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;

  position: fixed;
  z-index: 2147483000;
  bottom: 20px;
  font-family: var(--apaxir-font);
  color: var(--apaxir-text);
  line-height: 1.5;
  box-sizing: border-box;
}

:host([position="bottom-left"]) {
  left: 20px;
}

:host(:not([position="bottom-left"])) {
  right: 20px;
}

*, *::before, *::after {
  box-sizing: border-box;
}

button {
  font-family: inherit;
  cursor: pointer;
  border: none;
  background: none;
  color: inherit;
  padding: 0;
}

/* Launcher bubble */
.launcher {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--apaxir-primary);
  color: var(--apaxir-primary-contrast);
  box-shadow: var(--apaxir-shadow);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease;
}

.launcher:hover {
  transform: scale(1.06);
}

.launcher:active {
  transform: scale(0.96);
}

/* Panel */
.panel {
  display: none;
  flex-direction: column;
  width: 380px;
  height: 560px;
  max-height: calc(100vh - 40px);
  background: var(--apaxir-bg);
  border-radius: var(--apaxir-radius);
  box-shadow: var(--apaxir-shadow);
  overflow: hidden;
  animation: apaxir-pop 0.18s ease-out;
}

:host([data-open="true"]) .panel {
  display: flex;
}

:host([data-open="true"]) .launcher {
  display: none;
}

@keyframes apaxir-pop {
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

/* Header */
.header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 14px;
  background: var(--apaxir-primary);
  color: var(--apaxir-primary-contrast);
}

.header-logo {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  object-fit: cover;
  flex-shrink: 0;
}

.header-title {
  flex: 1;
  font-weight: 600;
  font-size: 15px;
}

.header-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  opacity: 0.9;
}

.header-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  opacity: 1;
}

/* Messages */
.messages {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--apaxir-bg-muted);
}

.bubble {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 14px;
  white-space: pre-wrap;
  word-wrap: break-word;
  animation: apaxir-fade-in 0.12s ease-out;
}

@keyframes apaxir-fade-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}

.bubble--assistant {
  align-self: flex-start;
  background: var(--apaxir-bg);
  color: var(--apaxir-text);
  border: 1px solid var(--apaxir-border);
  border-bottom-left-radius: 4px;
}

.bubble--user {
  align-self: flex-end;
  background: var(--apaxir-primary);
  color: var(--apaxir-primary-contrast);
  border-bottom-right-radius: 4px;
}

.bubble--error {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
  align-self: flex-start;
  border-bottom-left-radius: 4px;
}

/* Phase 3d.1 — citations footer inside assistant bubble */
.bubble__citation-hr {
  border: 0;
  border-top: 1px solid var(--apaxir-border);
  margin: 8px 0 6px;
}

.bubble__citations {
  font-size: 12px;
  color: var(--apaxir-text-muted);
  line-height: 1.4;
}

.bubble__citation-label {
  font-weight: 600;
}

.bubble__citation-link {
  color: var(--apaxir-text-muted);
  text-decoration: underline;
  text-underline-offset: 2px;
  word-break: break-word;
}

.bubble__citation-link[href]:hover {
  color: var(--apaxir-primary);
}

.bubble__citation-sep {
  margin: 0 2px;
  opacity: 0.7;
}

/* Suggested prompts */
.suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.suggestion {
  background: var(--apaxir-bg);
  color: var(--apaxir-primary);
  border: 1px solid var(--apaxir-border);
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 13px;
  transition: background 0.12s ease;
}

.suggestion:hover {
  background: var(--apaxir-primary);
  color: var(--apaxir-primary-contrast);
}

/* Typing indicator */
.typing {
  display: inline-flex;
  gap: 4px;
  align-items: center;
  padding: 4px 2px;
}

.typing span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--apaxir-text-muted);
  animation: apaxir-blink 1.2s infinite ease-in-out;
}

.typing span:nth-child(2) { animation-delay: 0.15s; }
.typing span:nth-child(3) { animation-delay: 0.3s; }

@keyframes apaxir-blink {
  0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
  40%           { opacity: 1;    transform: translateY(-3px); }
}

/* Input area */
.input-area {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px;
  background: var(--apaxir-bg);
  border-top: 1px solid var(--apaxir-border);
}

.input-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.input {
  width: 100%;
  min-height: 40px;
  max-height: 120px;
  padding: 10px 12px;
  font-family: inherit;
  font-size: 14px;
  color: var(--apaxir-text);
  background: var(--apaxir-bg-muted);
  border: 1px solid var(--apaxir-border);
  border-radius: 10px;
  outline: none;
  resize: none;
  overflow-y: auto;
  line-height: 1.4;
  box-sizing: border-box;
}

/* Phase 3f.1 — live char counter under the input. Hidden by default;
 * .visible toggled by JS once the user types past ~50% of the limit, so
 * the chrome stays clean for short questions. */
.char-counter {
  display: none;
  align-self: flex-end;
  font-size: 11px;
  line-height: 1;
  color: var(--apaxir-text-muted);
  padding: 0 4px;
  user-select: none;
}

.char-counter.visible {
  display: block;
}

.char-counter[data-state="warn"] {
  color: #b45309; /* amber-700 */
}

.char-counter[data-state="over"] {
  color: #b91c1c; /* red-700 */
  font-weight: 600;
}

.input:focus {
  border-color: var(--apaxir-primary);
}

.input:disabled {
  opacity: 0.6;
}

.send-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--apaxir-primary);
  color: var(--apaxir-primary-contrast);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.12s ease;
}

.send-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Error state — replaces launcher when bot cannot be loaded */
.error-state {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  max-width: 280px;
  background: #f3f4f6;
  color: #4b5563;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  font-size: 13px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  cursor: default;
  user-select: none;
}

.error-state svg {
  width: 14px;
  height: 14px;
  color: #9ca3af;
  flex-shrink: 0;
}

:host([data-open="true"]) .error-state {
  display: none;
}

/* Mobile */
@media (max-width: 480px) {
  :host {
    bottom: 0;
    right: 0;
    left: 0;
  }
  .launcher {
    position: absolute;
    right: 16px;
    bottom: 16px;
  }
  .panel {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
}
`;
